import React from "react";
import "./CDASHSDTMADaMCDISC.css";
import PageHeader from "../../../components/headers/PageHeader";
import wave from "../../../assets/Images/waves/wave_mou.png";

export default function CDASHSDTMADaMCDISC() {
    return (
        <div className="cdisc-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Regulatory-Grade Data Standards"
                title="CDASH, SDTM, ADaM & CDISC Submission Services"
            />

            <section className="cdisc-section cdisc-introSection">
                <h2>Regulatory-Ready CDISC Submissions Built with Precision</h2>

                <p>
                    Regulatory-grade CDISC submissions require deep experience in
                    SDTM, ADaM, submission packages, statistics, clinical operations,
                    and nonclinical standards such as SEND. Eupraxia supports sponsors
                    with end-to-end CDISC implementation, data standardization,
                    submission package development, regulatory application support,
                    and FDA remediation assistance.
                </p>

                <p>
                    Since 2018, Eupraxia has supported more than 750 studies, combining
                    regulatory inspection readiness, study-specific CDISC strategy,
                    and a gold-standard in-house multi-layered QC and review process.
                    With several global consulting organizations outsourcing their CDISC
                    development work to Eupraxia, the company has earned a distinct
                    position in the industry at a relatively young age.
                </p>

                <p>
                    Eupraxia is a CDISC Gold Member and supports clients throughout the
                    regulatory application process with compliant, traceable, and
                    submission-ready data deliverables.
                </p>
            </section>

            <section className="cdisc-section cdisc-cardSection">
                <h2>Our CDISC Implementation Services</h2>

                <div className="cdisc-cardGrid">
                    <article className="cdisc-card">
                        <h3>CDASH</h3>
                        <span className="cdisc-cardSubtitle">
                            Clinical Data Acquisition Standards Harmonization
                        </span>

                        <p>
                            We design and implement CDASH-compliant data collection
                            structures to improve upstream data consistency and downstream
                            regulatory readiness.
                        </p>

                        <ul className="cdisc-list">
                            <li>CRF design aligned with CDASH standards.</li>
                            <li>EDC build specifications.</li>
                            <li>Standardized data collection guidelines.</li>
                            <li>Edit checks and validation rules.</li>
                            <li>Mapping strategy from raw data to SDTM.</li>
                            <li>Cross-study data harmonization frameworks.</li>
                        </ul>

                        <p className="cdisc-cardNote">
                            Our CDASH implementation ensures that data is submission-ready
                            from the source.
                        </p>
                    </article>

                    <article className="cdisc-card">
                        <h3>SDTM</h3>
                        <span className="cdisc-cardSubtitle">
                            Study Data Tabulation Model
                        </span>

                        <p>
                            We convert raw clinical trial data into fully CDISC-compliant
                            SDTM datasets ready for regulatory submission.
                        </p>

                        <ul className="cdisc-list">
                            <li>SDTM dataset creation across all standard domains.</li>
                            <li>Custom domain creation for complex study designs.</li>
                            <li>Controlled terminology implementation.</li>
                            <li>Define.xml development and metadata management.</li>
                            <li>SDTM mapping specifications including annotated CRFs.</li>
                            <li>Data traceability and audit readiness.</li>
                        </ul>

                        <p className="cdisc-cardNote">
                            We ensure SDTM datasets are aligned with FDA Study Data
                            Technical Conformance Guide expectations.
                        </p>
                    </article>

                    <article className="cdisc-card">
                        <h3>ADaM</h3>
                        <span className="cdisc-cardSubtitle">
                            Analysis Data Model
                        </span>

                        <p>
                            We develop analysis-ready datasets that support statistical
                            analysis, TLF generation, and regulatory review.
                        </p>

                        <ul className="cdisc-list">
                            <li>ADaM dataset development including ADSL, BDS, and OCCDS.</li>
                            <li>Statistical Analysis Plan alignment.</li>
                            <li>Traceability from SDTM to ADaM to TLFs.</li>
                            <li>Derived variable creation and documentation.</li>
                            <li>Analysis metadata and ADRG preparation.</li>
                            <li>ISS and ISE integration datasets.</li>
                        </ul>

                        <p className="cdisc-cardNote">
                            Our ADaM packages support efficient regulatory review and
                            reproducible statistical analysis.
                        </p>
                    </article>
                </div>
            </section>

            <section className="cdisc-section cdisc-packageSection">
                <h2>CDISC Submission Package</h2>

                <p className="cdisc-sectionIntro">
                    We provide complete end-to-end CDISC submission packages structured
                    to meet global regulatory requirements across FDA, PMDA, and EMA
                    submission expectations.
                </p>

                <div className="cdisc-packageGrid">
                    <article className="cdisc-packageCard">SDTM datasets</article>
                    <article className="cdisc-packageCard">ADaM datasets</article>
                    <article className="cdisc-packageCard">Define.xml</article>
                    <article className="cdisc-packageCard">Reviewer’s Guides</article>
                    <article className="cdisc-packageCard">SDRG / ADRG</article>
                    <article className="cdisc-packageCard">Annotated CRFs</article>
                    <article className="cdisc-packageCard">Data validation reports</article>
                    <article className="cdisc-packageCard">
                        Pinnacle 21 validation outputs
                    </article>
                    <article className="cdisc-packageCard">Issue resolution support</article>
                    <article className="cdisc-packageCard">Traceability documentation</article>
                </div>
            </section>

            <section className="cdisc-section cdisc-governanceSection">
                <h2>Data Standards Governance &amp; Consulting</h2>

                <div className="cdisc-textBlock">
                    <h3>Study Data Standardization Plan</h3>

                    <p>
                        The Study Data Standardization Plan, or SDSP, is a living,
                        program-level document that defines the overarching strategy for
                        data standards, integration, analysis, and regulatory submissions
                        throughout the product development lifecycle.
                    </p>

                    <p>
                        The SDSP establishes the framework for implementing CDISC
                        standards such as CDASH, SDTM, ADaM, and SEND, along with
                        controlled terminology, coding dictionaries including MedDRA and
                        WHODrug, endpoint definitions, metadata standards, and traceability
                        requirements across clinical and nonclinical studies.
                    </p>

                    <p>
                        It also outlines data integration strategies for pooled analyses,
                        Integrated Summaries of Safety, Integrated Summaries of Efficacy,
                        cross-study analyses, and submission-ready data packages.
                    </p>

                    <p>
                        As development programs evolve, the SDSP is continuously maintained
                        to incorporate new studies, protocol amendments, emerging endpoints,
                        evolving regulatory requirements, and changes to industry standards.
                        This centralized governance framework improves consistency, reduces
                        downstream rework, strengthens inspection readiness, and supports
                        efficient preparation of high-quality submission packages.
                    </p>

                    <p>
                        Our SDSP services help sponsors establish a clear roadmap from study
                        start-up through regulatory submission, ensuring that data assets are
                        standardized, integrated, traceable, and inspection-ready.
                    </p>
                </div>
            </section>

            <section className="cdisc-section cdisc-frameworkSection">
                <h2>Scalable Data Standards Frameworks</h2>

                <p className="cdisc-sectionIntro">
                    We support sponsors in building reusable and scalable standards
                    frameworks that improve long-term efficiency across multiple
                    development programs.
                </p>

                <div className="cdisc-frameworkGrid">
                    <article className="cdisc-frameworkCard">
                        Enterprise CDISC implementation strategy
                    </article>

                    <article className="cdisc-frameworkCard">
                        SOPs for SDTM and ADaM development
                    </article>

                    <article className="cdisc-frameworkCard">
                        Metadata repository development
                    </article>

                    <article className="cdisc-frameworkCard">
                        Standard dataset and variable libraries
                    </article>

                    <article className="cdisc-frameworkCard">
                        Cross-study standardization strategy
                    </article>

                    <article className="cdisc-frameworkCard">
                        Legacy study conversion to CDISC
                    </article>
                </div>
            </section>

            <section className="cdisc-section cdisc-alignmentSection">
                <h2>CDISC and Regulatory Alignment</h2>

                <p className="cdisc-sectionIntro">
                    Our CDISC implementations are designed to support efficient regulatory
                    review by aligning data structures, metadata, documentation, and
                    traceability with global regulatory expectations.
                </p>

                <div className="cdisc-alignmentGrid">
                    <article className="cdisc-alignmentCard">
                        FDA Study Data Technical Conformance Guide
                    </article>

                    <article className="cdisc-alignmentCard">
                        CDISC Implementation Guides
                    </article>

                    <article className="cdisc-alignmentCard">
                        ICH E3, E6, and E9 guidelines
                    </article>

                    <article className="cdisc-alignmentCard">
                        PMDA electronic submission requirements
                    </article>

                    <article className="cdisc-alignmentCard">
                        EMA clinical data standards expectations
                    </article>
                </div>
            </section>
        </div>
    );
}